import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import parse from "html-react-parser";
import { connect } from "react-redux";
import "./BlogDetails.css";
import AddBlogComments from "./AddBlogComments";
import { getBlogById, getBlogCategories } from "../../redux/actions/API/blogs";
import { UserOutlined } from "@ant-design/icons";

import { getApprovedBlogComments } from "../../redux/actions/API/commentActions";
import ApprovedBlogCommentList from "./ApprovedBlogCommentList";
import Loader from "../Loader/Loader";
import { Avatar, Card } from "antd";
import { Helmet } from "react-helmet-async";

interface IBlogById {
  getBlogById: Function;
  blogDataById: any;
  getBlogCategories: Function;
  getBlogLoading: boolean;
}

const BlogDetails: React.FC<IBlogById> = ({
  getBlogById,
  blogDataById,
  getBlogCategories,
  getBlogLoading
}) => {
  const { id, title } = useParams<{ id: string, title: string }>();
  const [loading, setLoading] = useState<boolean>(true);
  const [tocList, setTocList] = useState<{ id: string; text: string }[]>([]);
  const [parsedContent, setParsedContent] = useState<string>("");
  const HEADER_BG_COLOR = "#D0E8FF";

  useEffect(() => {
    if (!getBlogLoading) setLoading(false);
  }, [getBlogLoading])

  useEffect(() => {
    window.scrollTo(0, 0);
    if (id) {
      getBlogById({ id, title });
      getApprovedBlogComments({ blogId: Number(id) });
    }
  }, [id, getBlogById]);

  useEffect(() => {
    if (!blogDataById?.content) return;

    const wrapper = document.createElement("div");
    wrapper.innerHTML = blogDataById.content;

    const strongTags = wrapper.querySelectorAll("strong");
    const tocEntries: { id: string; text: string }[] = [];

    strongTags.forEach((tag, index) => {
      const textContent = tag.textContent?.trim();
      if (textContent && textContent.length > 3 && textContent.length < 60) {
        const id = `toc-item-${index}`;

        const parentBlock = tag.closest("p") || tag.closest("div") || tag;
        parentBlock.setAttribute("id", id);

        tocEntries.push({
          id,
          text: textContent.replace(/\u00a0/g, " "),
        });
      }
    });

    setTocList(tocEntries);
    setParsedContent(wrapper.innerHTML);
  }, [blogDataById]);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          let currentId = "";

          for (const { id } of tocList) {
            const element = document.getElementById(id);
            if (element) {
              const offsetTop = element.getBoundingClientRect().top;
              if (offsetTop < 150) {
                currentId = id;
              }
            }
          }

          const links = document.querySelectorAll(".blog-toc a");
          links.forEach((link) => link.classList.remove("active-toc-link"));

          if (currentId) {
            const activeLink = document.querySelector(
              `.blog-toc a[href="#${currentId}"]`
            );
            activeLink?.classList.add("active-toc-link");
          }

          ticking = false;
        });

        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [tocList]);

  const renderBlog = (blog: any) => {
    if (!blog) return <div>No blog found</div>;

    const updatedAtDate = new Date(blog.updatedAt).toLocaleDateString();

    return (
      <div className="blog-content-main">
        <Helmet>
          <title>Understand Bill of Materials with Ease Margin Guide</title>
          <meta
            name="description"
            content="Learn everything about Bill of Materials (BOM), its importance in manufacturing, and how it streamlines production processes efficiently and accurately."
          />
          <meta
            name="keywords"
            content="BOM, Bill of Materials, manufacturing, Ease Margin, production planning"
          />
          <meta property="og:title" content="Understand Bill of Materials with Ease Margin Guide" />
          <meta
            property="og:description"
            content="Learn everything about Bill of Materials (BOM), its importance in manufacturing, and how it streamlines production processes efficiently and accurately."
          />
          <meta property="og:url" content="https://easemargin.com/blog_details/Technology/Let-Us-Understand-Bill-Of-Materials" />
          <link
            rel="canonical"
            href="https://easemargin.com/blog_details/Technology/Let-Us-Understand-Bill-Of-Materials"
          />
        </Helmet>

        <div className="shortdesc-image-wrapper">
          <div className="short-desc-wrapper">
            <h2 className="blog-title">{blog.title}</h2>
            <div className="short-desc">{blog.shortDesc}</div>

            {/* Desktop only meta */}
            <div className="blog-post-meta inside-meta desktop-meta">
              <ul className="post-meta-list">
                <li>
                  <p
                    className="meta-author"
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    By {blog.author}
                  </p>
                </li>
                <li className="meta-divider">
                  <span className="flaticon-minus" />
                </li>
                <li>
                  <p className="meta-date">{updatedAtDate}</p>
                </li>
              </ul>
            </div>
          </div>

          <div className="blog-post-img">
            <img src={blog.imageURL} alt="blog-post" />
          </div>

          {/* Mobile only meta */}
          <div className="blog-post-meta inside-meta mobile-meta">
            <ul className="post-meta-list">
              <li>
                <p
                  className="meta-author"
                  style={{ display: "flex", alignItems: "center", gap: 8 }}
                >
                  <Avatar
                    size={60}
                    src={`https://api.dicebear.com/7.x/miniavs/svg?seed=${0}`}
                    icon={!blog.authorAvatarUrl && <UserOutlined />}
                    alt={blog.author}
                  />
                  By {blog.author}
                </p>
              </li>
              <li className="meta-divider">
                <span className="flaticon-minus" />
              </li>
              <li>
                <p className="meta-date">{updatedAtDate}</p>
              </li>
            </ul>
          </div>
        </div>

        <div className="blog-main-wrapper">
          <div className="container">
            <div className="blog-layout">
              <div className="blog-content-main">
                <div className="blog-parsed-content">
                  {parse(parsedContent)}
                  <Card
                    bordered={false}
                    style={{
                      margin: "24px 0",
                      borderRadius: "16px",
                    }}
                  >
                    <h4>Leave us a comment</h4>
                    <AddBlogComments />
                    <ApprovedBlogCommentList />
                  </Card>
                </div>
              </div>

              {tocList.length > 0 && (
                <div className="blog-toc">
                  <h4>Table of Contents</h4>
                  <ul>
                    {tocList.map((item) => (
                      <li key={item.id}>
                        <a href={`#${item.id}`}>{item.text}</a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Loader loading={loading || getBlogLoading} />
      {(!loading && !getBlogLoading) && <>
        <Header bgColor={HEADER_BG_COLOR} />
        <div className="">
          <div className="blog-content-main">
            {blogDataById ? renderBlog(blogDataById) : <div>No blog found</div>}
          </div>
        </div>
        <Footer />
      </>}
    </>
  );
};

const mapStateToProps = (state: any) => ({
  blogDataById: state.api.getBlogById.data,
  getBlogLoading: state.api.getBlogById.loading,
});

const mapDispatchToProps = (dispatch: any) => ({
  getBlogById: (id: number) => dispatch(getBlogById(id)),
  getBlogCategories: (category: string) =>
    dispatch(getBlogCategories(category)),
});

export default connect(mapStateToProps, mapDispatchToProps)(BlogDetails);
